import { useState, useEffect, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/services/api';
import type { AccountClosureStatus, ConsentDataRequests, DataRequestKind, DataRequestResult } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useCompanionName } from '@/hooks/useCompanionName';
import { useTimezoneSetting } from '@/hooks/useTimezoneSetting';
import { useToast } from '@/components/ui/use-toast';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHead, Emphasis } from '@/components/editorial';
import { NAV_KEY } from '@/i18n/keys/nav';
import {
  Bell,
  ChevronRight,
  Loader2,
  Sun,
  MessageSquare,
  User,
} from 'lucide-react';
import type { ConsentSettings, ImplicitHandoffConsent, NotificationSettings, PushCategorySettings } from '@/types/api';
import { Capacitor } from '@capacitor/core';
import { useQueryClient } from '@tanstack/react-query';
import { useFamilyMine } from '@/hooks/useFamilyMine';
import { useHandoffFamilyEntry } from '@/hooks/useHandoffFamilyEntry';
import {
  getTheme,
  applyTheme,
  getSidebarMode,
  getSidebarSide,
  type ThemeMode,
  type SidebarMode,
  type SidebarSide,
} from '@/components/settings/settings-storage';
import { AppearanceSection } from '@/components/settings/sections/AppearanceSection';
import { CompanionSection } from '@/components/settings/sections/CompanionSection';
import { NotificationsSection } from '@/components/settings/sections/NotificationsSection';
import { AccountSection, type PatientFormState } from '@/components/settings/sections/AccountSection';
import { LanguageSelect } from '@/components/settings/LanguageSelect';


const SETTINGS_TABS = ['appearance', 'account', 'companion', 'notifications'] as const;
type SettingsTab = (typeof SETTINGS_TABS)[number];
const APPLICANT_SETTINGS_TABS: readonly SettingsTab[] = ['appearance', 'account'];

export function SettingsPage() {
  const { toast } = useToast();
  // #2109 R1 (L4): this page's own strings live in `locales/en/settings.json`;
  // the eyebrow is the nav row's key (rulings brief B7).
  const { t } = useTranslation('settings');
  const { user } = useAuth();
  const companionName = useCompanionName();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Timezone (shared with staff Settings). Blank means "browser", #2741.
  const { timezone, setTimezone, saving: profileSaving, save: saveTimezone } = useTimezoneSetting();

  // ── Patient (parent role only) ────────────────────────────────────
  const queryClient = useQueryClient();
  const { data: familyData } = useFamilyMine();
  const patient = familyData?.data?.patient;

  const [patientForm, setPatientForm] = useState<PatientFormState>({
    name: '',
    date_of_birth: '',
    diagnosis: '',
    treatment_phase: '',
    hospital: '',
    cycle_number: '',
    cycle_started_at: '',
    cycle_length_days: '',
  });
  const [patientSaving, setPatientSaving] = useState(false);

  useEffect(() => {
    if (!patient) return;
    setPatientForm({
      name: patient.name ?? '',
      date_of_birth: patient.date_of_birth ? patient.date_of_birth.slice(0, 10) : '',
      diagnosis: patient.diagnosis ?? '',
      treatment_phase: patient.treatment_phase ?? '',
      hospital: patient.hospital ?? '',
      cycle_number: patient.cycle_number != null ? String(patient.cycle_number) : '',
      cycle_started_at: patient.cycle_started_at ? patient.cycle_started_at.slice(0, 10) : '',
      cycle_length_days: patient.cycle_length_days != null ? String(patient.cycle_length_days) : '',
    });
  }, [patient]);

  // ── Stevens auto-tasks (parent role only) ─────────────────────────
  const autoTasks = familyData?.data?.auto_task_preferences;
  const [autoSaving, setAutoSaving] = useState(false);

  const updateAutoTask = async (
    payload: Partial<{ enabled: boolean; rules: Partial<{ refill_prep: boolean; post_chemo_recovery: boolean; appointment_prep: boolean }> }>,
  ) => {
    setAutoSaving(true);
    try {
      await api.families.updateAutoTaskPreferences(payload);
      await queryClient.invalidateQueries({ queryKey: ['family-mine'] });
    } catch {
      toast({ variant: 'destructive', title: t('toasts.autoTasksFailed', { companionName }), description: t('toasts.autoTasksFailedDescription') });
    } finally {
      setAutoSaving(false);
    }
  };

  const handlePatientSave = async () => {
    setPatientSaving(true);
    try {
      const num = (s: string) => (s.trim() === '' ? null : parseInt(s, 10));
      await api.families.updatePatient({
        name: patientForm.name || undefined,
        date_of_birth: patientForm.date_of_birth || null,
        diagnosis: patientForm.diagnosis || undefined,
        treatment_phase: patientForm.treatment_phase || undefined,
        hospital: patientForm.hospital || undefined,
        cycle_number: num(patientForm.cycle_number),
        cycle_started_at: patientForm.cycle_started_at || null,
        cycle_length_days: num(patientForm.cycle_length_days),
      });
      await queryClient.invalidateQueries({ queryKey: ['family-mine'] });
      toast({ title: t('toasts.patientSaved') });
    } catch {
      toast({ variant: 'destructive', title: t('toasts.patientFailed'), description: t('toasts.notSavedDescription') });
    } finally {
      setPatientSaving(false);
    }
  };

  // Appearance
  const [theme, setTheme] = useState<ThemeMode>(getTheme);

  // Display
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>(getSidebarMode);
  const [sidebarModeDirty, setSidebarModeDirty] = useState(false);
  const [sidebarSide, setSidebarSide] = useState<SidebarSide>(getSidebarSide);

  // Tabs — active tab is driven by the ?tab= query param (deep-linkable).
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  // DECISIONS 2026-10-16: an applicant reaches the pre-screen, the
  // application and its status page, never the companion. Settings shows
  // them Appearance and Account only.
  const isApplicant = user?.role === 'applicant';
  const availableTabs: readonly SettingsTab[] = isApplicant ? APPLICANT_SETTINGS_TABS : SETTINGS_TABS;
  const activeTab: SettingsTab = (availableTabs as readonly string[]).includes(rawTab ?? '')
    ? (rawTab as SettingsTab)
    : 'appearance';
  const handleTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  // Consent (stevens_paused / outbound_disabled / circle_sharing_default
  // removed under D-7, #642 — they persisted values with zero enforcement
  // readers).
  // trfer_conversation_access defaults OFF (#643): it is family-owned consent
  // read from Family.consent_settings — the field the staff gates enforce,
  // which fails closed — so the pre-load display must not claim it is on.
  const [consent, setConsent] = useState<ConsentSettings>({
    trfer_conversation_access: false,
  });

  // Notifications
  const [notifications, setNotifications] = useState<NotificationSettings>({
    email: true,
    sms: false,
    push: true,
    push_categories: {
      handoff_nudge: true,
      trfer_claimed: true,
      trfer_message: true,
      handback: true,
      task_due: true,
      grant_decision: true,
      calendar_reminder: true,
    },
  });

  // Family-level implicit-handoff consent (gates auto-alerts to TRF). Defaults
  // match the Family.create() defaults — overwritten by the GET response.
  const [implicitHandoff, setImplicitHandoff] = useState<ImplicitHandoffConsent>({
    lifecycle: true,
    claim_stress: true,
  });
  // #3504: the auto-alert card shows only while the family-entry handoff flag
  // is lit — its two signals create nothing while it is dark.
  const { enabled: handoffFamilyEntryEnabled } = useHandoffFamilyEntry({ active: user?.role === 'parent' });

  // MFA
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaToggling, setMfaToggling] = useState(false);
  const [password, setPassword] = useState('');
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [showEnrollCodeInput, setShowEnrollCodeInput] = useState(false);
  const [mfaEnrollCode, setMfaEnrollCode] = useState('');

  // Data requests (#2391): the OPEN export / deletion requests, hydrated from
  // GET /settings/consent so a pending one survives a refresh, and updated
  // by this visit's clicks.
  const [dataRequests, setDataRequests] = useState<ConsentDataRequests>({});

  // #2390: a scheduled account closure, read back so this tab can name it.
  // Fail-soft and outside the Promise.all below: a failed read leaves the
  // row as the plain way in to /account/close, which reads it again.
  const [closure, setClosure] = useState<AccountClosureStatus | null>(null);
  useEffect(() => {
    let live = true;
    // Started inside a promise so a synchronous throw lands in .catch too.
    Promise.resolve()
      .then(() => api.account.closeStatus())
      .then((r) => { if (live && r.status === 'success' && r.data) setClosure(r.data); })
      .catch(() => { /* the row still links to /account/close */ });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    Promise.all([api.consent.get(), api.mfa.status()])
      .then(([consentRes, mfaRes]) => {
        if (consentRes.status === 'success' && consentRes.data) {
          setConsent(consentRes.data.consent);
          setNotifications(consentRes.data.notifications);
          if (consentRes.data.implicit_handoff) {
            setImplicitHandoff(consentRes.data.implicit_handoff);
          }
          if (consentRes.data.data_requests) {
            setDataRequests(consentRes.data.data_requests);
          }
        }
        if (mfaRes.status === 'success' && mfaRes.data) {
          setMfaEnabled(mfaRes.data.mfa_enabled);
        }
      })
      .catch(() => toast({ variant: 'destructive', title: t('toasts.loadFailed'), description: t('toasts.loadFailedDescription') }))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount-only; toast and t refs excluded intentionally

  const handleThemeChange = (value: ThemeMode) => {
    setTheme(value);
    applyTheme(value);
  };

  const handleSidebarModeChange = (value: SidebarMode) => {
    setSidebarMode(value);
    setSidebarModeDirty(true);
  };

  const handleSidebarModeSave = () => {
    window.dispatchEvent(new CustomEvent('trf:sidebarModeChanged', { detail: sidebarMode }));
    setSidebarModeDirty(false);
    toast({ title: t('toasts.displaySaved') });
  };

  // Sidebar side applies instantly (binary, instant feedback) — MainLayout's
  // trf:sidebarSideChanged listener persists it, mirroring the mode pattern.
  const handleSidebarSideChange = (value: SidebarSide) => {
    setSidebarSide(value);
    window.dispatchEvent(new CustomEvent('trf:sidebarSideChanged', { detail: value }));
  };

  const updateConsent = useCallback(async (patch: Record<string, unknown>) => {
    setSaving(true);
    try {
      const res = await api.consent.update(patch);
      if (res.status === 'success' && res.data) {
        setConsent(res.data.consent);
        setNotifications(res.data.notifications);
        // implicit_handoff is null when this PATCH didn't touch family consent.
        if (res.data.implicit_handoff) {
          setImplicitHandoff(res.data.implicit_handoff);
        }
      }
      toast({ title: t('toasts.settingsSaved') });
    } catch {
      toast({ variant: 'destructive', title: t('toasts.settingsFailed'), description: t('toasts.notSavedDescription') });
    } finally {
      setSaving(false);
    }
  }, [toast, t]);

  // Per-category push opt-in (M3.11). Web users never see these toggles —
  // gated by Capacitor.isNativePlatform() at the JSX level. Defensive default
  // keeps the UI working if the server omits push_categories on first GET.
  const isNative = Capacitor.isNativePlatform();
  const pushCategories = notifications.push_categories ?? {
    handoff_nudge: true,
    trfer_claimed: true,
    trfer_message: true,
    handback: true,
    task_due: true,
    grant_decision: true,
    calendar_reminder: true,
  };
  const updatePushCategory = useCallback(
    (key: keyof PushCategorySettings, value: boolean) => {
      // Partial-key payload — api.consent.update accepts Partial<PushCategorySettings>
      // but the typed wrapper expects the full shape, so cast at the boundary.
      updateConsent({ push_categories: { [key]: value } } as Record<string, unknown>);
    },
    [updateConsent],
  );

  const handleMfaToggle = async (checked: boolean) => {
    if (checked) {
      setMfaToggling(true);
      try {
        await api.mfa.enrollStart('email');
        setMfaEnrollCode('');
        setShowEnrollCodeInput(true);
      } catch {
        toast({ variant: 'destructive', title: t('toasts.mfaCodeFailed'), description: t('toasts.tryAgainDescription') });
      } finally {
        setMfaToggling(false);
      }
    } else {
      setShowPasswordInput(true);
    }
  };

  const handleMfaEnrollVerify = async () => {
    if (mfaEnrollCode.length !== 6) return;
    setMfaToggling(true);
    try {
      await api.mfa.enrollVerify('email', mfaEnrollCode);
      setMfaEnabled(true);
      setShowEnrollCodeInput(false);
      setMfaEnrollCode('');
      toast({ title: t('toasts.mfaEnabled'), description: t('toasts.mfaEnabledDescription') });
    } catch {
      toast({ variant: 'destructive', title: t('toasts.mfaInvalidCode'), description: t('toasts.tryAgainDescription') });
    } finally {
      setMfaToggling(false);
    }
  };

  const handleMfaDisable = async () => {
    if (!password) return;
    setMfaToggling(true);
    try {
      await api.mfa.disable(password);
      setMfaEnabled(false);
      setShowPasswordInput(false);
      setPassword('');
      toast({ title: t('toasts.mfaDisabled') });
    } catch {
      toast({ variant: 'destructive', title: t('toasts.mfaDisableFailed'), description: t('toasts.mfaDisableFailedDescription') });
    } finally {
      setMfaToggling(false);
    }
  };

  // #646: both requests are recorded server-side and notify TRF staff. The
  // copy promises exactly that — a human follow-up. No timeline, no artifact:
  // the export/deletion pipelines do not exist yet (filed as future work).
  // #2391: the server dedupes an open request (`already_open`); the toast
  // now says so instead of reading as a new submission.
  const recordRequest = (kind: DataRequestKind, data: DataRequestResult | undefined) => {
    // A success with no body still recorded a request on the server.
    setDataRequests((prev) => ({
      ...prev,
      [kind]: {
        request_id: data?.request_id ?? prev[kind]?.request_id ?? '',
        kind,
        status: data?.status ?? 'open',
        requested_at: prev[kind]?.requested_at ?? null,
      },
    }));
  };

  const handleDataExport = async () => {
    try {
      const res = await api.consent.requestExport();
      recordRequest('export', res.data);
      toast(res.data?.already_open
        ? { title: t('toasts.exportAlreadyOpen'), description: t('toasts.alreadyOpenDescription') }
        : { title: t('toasts.exportRequested'), description: t('toasts.recordedDescription') });
    } catch {
      toast({ variant: 'destructive', title: t('toasts.exportFailed'), description: t('toasts.tryAgainDescription') });
    }
  };

  const handleDataDeletion = async () => {
    try {
      const res = await api.consent.requestDeletion();
      recordRequest('deletion', res.data);
      toast(res.data?.already_open
        ? { title: t('toasts.deletionAlreadyOpen'), description: t('toasts.alreadyOpenDescription') }
        : { title: t('toasts.deletionSubmitted'), description: t('toasts.recordedDescription') });
    } catch {
      toast({ variant: 'destructive', title: t('toasts.deletionFailed'), description: t('toasts.tryAgainDescription') });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-stevens-ink-3" />
      </div>
    );
  }

  const isParent = user?.role === 'parent';

  return (
    <div className="container max-w-2xl py-8 space-y-6">
      <PageHead
        eyebrowKey={NAV_KEY.settings}
        title={<Trans t={t} i18nKey="title" components={{ em: <Emphasis>{null}</Emphasis> }} />}
        lede={t('lede')}
      />

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        {/* Below sm the triggers are icon-over-short-label (the BottomNav
            grammar): icons carry recognition, so the long words give way to
            labels that genuinely fit 4-up at 320px ("Display", "Alerts", the
            companion's own name). Equal columns + tiny text alone clip at
            320px (50px columns vs 58-62px words). sm+ keeps the standard
            text-only tab row. */}
        <TabsList className={`grid h-auto w-full ${isApplicant ? 'grid-cols-2' : 'grid-cols-4'} sm:h-10`}>
          {([
            { value: 'appearance', icon: Sun, mobile: t('tabs.appearanceShort'), full: t('tabs.appearance') },
            { value: 'account', icon: User, mobile: t('tabs.account'), full: t('tabs.account') },
            { value: 'companion', icon: MessageSquare, mobile: companionName, full: t('tabs.companion') },
            { value: 'notifications', icon: Bell, mobile: t('tabs.notificationsShort'), full: t('tabs.notifications') },
          ] as const).filter(({ value }) => availableTabs.includes(value)).map(({ value, icon: Icon, mobile, full }) => (
            <TabsTrigger
              key={value}
              value={value}
              className="min-w-0 flex-col gap-0.5 px-0.5 py-1.5 text-label leading-tight sm:flex-row sm:px-3 sm:py-1.5 sm:text-body"
            >
              <Icon className="h-4 w-4 sm:hidden" aria-hidden="true" />
              <span className="max-w-full truncate sm:hidden">{mobile}</span>
              <span className="hidden sm:inline">{full}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="appearance" className="space-y-6">
          {/* #2109 R1 (L4): the UI language — User.preferences.language — sits
              on the default tab so it is the first thing a reader who cannot
              read the page finds. The companion voice language stays under
              Companion; they are different fields (rulings brief B3). */}
          <LanguageSelect />
          <AppearanceSection
            theme={theme}
            onThemeChange={handleThemeChange}
            sidebarMode={sidebarMode}
            sidebarModeDirty={sidebarModeDirty}
            onSidebarModeChange={handleSidebarModeChange}
            onSidebarModeSave={handleSidebarModeSave}
            sidebarSide={sidebarSide}
            onSidebarSideChange={handleSidebarSideChange}
          />
        </TabsContent>

        <TabsContent value="account" className="space-y-6">
          <AccountSection
            user={user ?? null}
            companionName={companionName}
            timezone={timezone}
            onTimezoneChange={setTimezone}
            profileSaving={profileSaving}
            onProfileSave={saveTimezone}
            isParent={isParent}
            patient={patient}
            patientForm={patientForm}
            setPatientForm={setPatientForm}
            patientSaving={patientSaving}
            onPatientSave={handlePatientSave}
            consent={consent}
            saving={saving}
            onUpdateConsent={updateConsent}
            dataRequests={dataRequests}
            onDataExport={handleDataExport}
            onDataDeletion={handleDataDeletion}
            closure={closure}
            mfaEnabled={mfaEnabled}
            mfaToggling={mfaToggling}
            onMfaToggle={handleMfaToggle}
            showEnrollCodeInput={showEnrollCodeInput}
            setShowEnrollCodeInput={setShowEnrollCodeInput}
            mfaEnrollCode={mfaEnrollCode}
            setMfaEnrollCode={setMfaEnrollCode}
            onMfaEnrollVerify={handleMfaEnrollVerify}
            showPasswordInput={showPasswordInput}
            setShowPasswordInput={setShowPasswordInput}
            password={password}
            setPassword={setPassword}
            onMfaDisable={handleMfaDisable}
          />
        </TabsContent>

        {!isApplicant && (
        <>
        <TabsContent value="companion" className="space-y-6">
          <CompanionSection
            companionName={companionName}
            autoTasks={autoTasks}
            autoSaving={autoSaving}
            onUpdateAutoTask={updateAutoTask}
            userRole={user?.role}
          />
        </TabsContent>

        <TabsContent value="notifications" className="space-y-6">
          <NotificationsSection
            companionName={companionName}
            notifications={notifications}
            saving={saving}
            onUpdateConsent={updateConsent}
            isNative={isNative}
            pushCategories={pushCategories}
            onUpdatePushCategory={updatePushCategory}
            isParent={isParent}
            implicitHandoff={implicitHandoff}
            handoffFamilyEntryEnabled={handoffFamilyEntryEnabled}
          />
        </TabsContent>
        </>
        )}
      </Tabs>

      {/* Legal — a row, deliberately outside the tab set.

          `LegalStrip` is hidden below `md` (design grammar §4.8), so on a
          phone this row is the only path by navigation to the record of what
          this account agreed to and when. It also closes the §4.2 orphan:
          `/settings/legal` had zero inbound links anywhere in the app and was
          reachable only by typing the URL. */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>{t('legal.heading')}</CardTitle>
          <CardDescription>{t('legal.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Link
            to="/settings/legal"
            className="inline-flex items-center gap-1.5 text-body font-medium text-stevens-indigo transition-colors hover:text-stevens-indigo-2"
          >
            {t('legal.link')}
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
